/**
 * components/Layout/menuConfig.tsx - 菜单配置和权限逻辑
 *
 * 功能说明：
 * - 定义应用菜单结构
 * - 集成权限过滤功能
 *
 * 依赖关系：
 * - 依赖 antd Menu
 * - 依赖 @ant-design/icons
 * - 依赖 permissions/menuPermissions.ts
 *
 * 关键注释索引：
 * - [菜单配置] - 见下方 menuConfigs
 * - [菜单获取] - 见下方 getMenuItems
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 * 重构日期：2026-05-03 - 拆分为 permissions 模块
 */

import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  ShoppingOutlined,
  BarChartOutlined,
  SettingOutlined,
  DatabaseOutlined,
  DollarOutlined,
  TeamOutlined,
  SafetyOutlined,
  ApartmentOutlined,
  FormOutlined,
  PlusSquareOutlined,
  WarningOutlined,
  PieChartOutlined,
  InboxOutlined,
  SwapOutlined,
  CheckCircleOutlined,
  UserOutlined,
  HistoryOutlined,
  FileTextOutlined,
  ShopOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import type { MenuItemConfig } from './permissions/menuPermissions';
import { checkPermission, filterMenuItems } from './permissions/menuPermissions';

type MenuItem = Required<MenuProps>['items'][number];
type MenuItemLeaf = Exclude<MenuItem, null>;

// 菜单项配置类型（带权限）- 兼容 antd MenuItem 类型
export type MenuItemConfigWithPermission = {
  key: string;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  permission?: string | string[] | null;
  children?: MenuItemConfigWithPermission[];
  [key: string]: any;
};

/**
 * [菜单配置] 应用完整菜单结构
 *
 * @description 定义所有菜单项及其权限要求
 * - key: 路由路径
 * - icon: 菜单图标
 * - label: 显示名称
 * - permission: 访问权限要求
 * - children: 子菜单项
 */
const menuConfigs: MenuItemConfigWithPermission[] = [
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    label: '项目总览',
    permission: null, // 所有人可见
  },
  {
    key: '/project',
    icon: <ProjectOutlined />,
    label: '项目管理',
    permission: 'project:view',
  },
  {
    key: 'third-party',
    icon: <ShopOutlined />,
    label: '第三方管理',
    permission: ['supplier:manage', 'project:view'],
    children: [
      {
        key: '/supplier-management',
        icon: <ShopOutlined />,
        label: '供应商管理',
        permission: 'supplier:manage',
      },
      {
        key: '/equipment-provider-management',
        icon: <ToolOutlined />,
        label: '设备提供方管理',
        permission: 'supplier:manage',
      },
      {
        key: '/subcontractor-management',
        icon: <TeamOutlined />,
        label: '分包商管理',
        permission: 'supplier:manage',
      },
      {
        key: '/subcontract-cost-management',
        icon: <SnippetsOutlined />,
        label: '分包成本管理',
        permission: 'project:view',
      },
      {
        key: '/payment-approval',
        icon: <SafetyOutlined />,
        label: '付款审批',
        permission: 'project:view',
      },
      {
        key: '/coordination-cost-management',
        icon: <ApartmentOutlined />,
        label: '协调成本管理',
        permission: 'project:view',
      },
      {
        key: '/project-contract-management',
        icon: <FileTextOutlined />,
        label: '合同管理',
        permission: 'contract:view',
      },
    ],
  },
  {
    key: 'worker',
    icon: <TeamOutlined />,
    label: '工人记录与管理',
    permission: null,
    children: [
      {
        key: '/worker-manage',
        icon: <UserOutlined />,
        label: '工人列表',
        permission: 'worker:view',
      },
      {
        key: '/worker-history',
        icon: <HistoryOutlined />,
        label: '出工记录',
        permission: 'worktime:view',
      },
      {
        key: '/batch-semantic-parse',
        icon: <FormOutlined />,
        label: '工时批量录入',
        permission: 'worktime:create',
      },
      {
        key: '/worktime-batch-history',
        icon: <HistoryOutlined />,
        label: '批量提交历史',
        permission: 'worktime:view',
      },
    ],
  },
  {
    key: 'material-records',
    icon: <ShoppingOutlined />,
    label: '材料记录与管理',
    permission: null,
    children: [
      {
        key: '/recognition-management',
        icon: <FileTextOutlined />,
        label: '材料识别',
        permission: 'material:view',
      },
      {
        key: '/material',
        icon: <ShoppingOutlined />,
        label: '材料使用',
        permission: 'material:view',
      },
      {
        key: '/transfer',
        icon: <SwapOutlined />,
        label: '材料调拨',
        permission: 'transfer:view',
      },
      {
        key: '/inventory',
        icon: <InboxOutlined />,
        label: '库存管理',
        permission: 'inventory:view',
      },
    ],
  },
  {
    key: '/equipment',
    icon: <ToolOutlined />,
    label: '设备使用',
    permission: 'equipment:view',
  },
  {
    key: 'progress',
    icon: <ApartmentOutlined />,
    label: '进度跟踪',
    permission: 'progress:view',
    children: [
      {
        key: '/progress/work-items',
        icon: <ApartmentOutlined />,
        label: '工程项管理',
        permission: 'progress:view',
      },
      {
        key: '/progress/records',
        icon: <FormOutlined />,
        label: '进度填报',
        permission: 'progress:view',
      },
      {
        key: '/progress/additions',
        icon: <PlusSquareOutlined />,
        label: '增项管理',
        permission: 'progress:view',
      },
      {
        key: '/progress/rectifications',
        icon: <WarningOutlined />,
        label: '整改管理',
        permission: 'progress:view',
      },
      {
        key: '/progress/reports/progress',
        icon: <PieChartOutlined />,
        label: '进度报表',
        permission: 'report:view',
      },
      {
        key: '/progress/reports/additions',
        icon: <BarChartOutlined />,
        label: '增项报表',
        permission: 'report:view',
      },
      {
        key: '/progress/reports/rectifications',
        icon: <BarChartOutlined />,
        label: '整改报表',
        permission: 'report:view',
      },
    ],
  },
  {
    key: '/reports',
    icon: <BarChartOutlined />,
    label: '报表分析',
    permission: 'report:view',
  },
  {
    key: 'reports-new',
    icon: <PieChartOutlined />,
    label: '报表中心',
    permission: 'report:view',
    children: [
      {
        key: '/reports-new/dashboard',
        icon: <DashboardOutlined />,
        label: '经营分析',
        permission: ['report:dashboard:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/cost',
        icon: <DollarOutlined />,
        label: '成本报表',
        permission: ['report:cost:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/progress',
        icon: <ApartmentOutlined />,
        label: '进度报表',
        permission: ['report:progress:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/material',
        icon: <ShoppingOutlined />,
        label: '材料报表',
        permission: ['report:material:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/worktime',
        icon: <ClockCircleOutlined />,
        label: '工时报表',
        permission: ['report:worktime:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/inventory',
        icon: <InboxOutlined />,
        label: '库存报表',
        permission: ['report:inventory:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/equipment',
        icon: <ToolOutlined />,
        label: '设备报表',
        permission: ['report:equipment:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/change-order',
        icon: <PlusSquareOutlined />,
        label: '增项报表',
        permission: ['report:changeorder:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/rectification',
        icon: <WarningOutlined />,
        label: '整改报表',
        permission: ['report:rectification:view', 'report:view', 'report:manage'],
      },
      {
        key: '/reports-new/transfer',
        icon: <SwapOutlined />,
        label: '调拨报表',
        permission: ['report:transfer:view', 'report:view', 'report:manage'],
      },
    ],
  },
  {
    key: 'admin',
    icon: <SettingOutlined />,
    label: '系统管理',
    permission: 'system:manage',
    children: [
      {
        key: '/data-management',
        icon: <DatabaseOutlined />,
        label: '数据管理',
        permission: 'system:manage',
      },
      {
        key: '/price-config',
        icon: <DollarOutlined />,
        label: '价格配置',
        permission: 'system:manage',
      },
      {
        key: '/user-management',
        icon: <TeamOutlined />,
        label: '用户管理',
        permission: 'system:manage',
      },
      {
        key: '/user-permission-manage',
        icon: <SafetyOutlined />,
        label: '用户权限管理',
        permission: 'system:manage',
      },
      {
        key: '/permission-matrix',
        icon: <SafetyOutlined />,
        label: '权限矩阵',
        permission: 'system:manage',
      },
      {
        key: '/data-validation',
        icon: <CheckCircleOutlined />,
        label: '数据验证',
        permission: 'system:manage',
      },
      {
        key: '/operation-records',
        icon: <HistoryOutlined />,
        label: '操作记录',
        permission: null,
      },
      {
        key: '/soft-delete-recovery',
        icon: <FileTextOutlined />,
        label: '数据恢复',
        permission: null,
      },
    ],
  },
];

/**
 * [菜单获取] 获取过滤后的菜单项
 *
 * @description 根据用户权限过滤菜单
 * - 如果没有权限信息，只显示无权限要求的菜单
 * - 否则根据用户权限过滤所有菜单
 *
 * @param userPermissions - 用户权限列表
 * @returns 过滤后的菜单项
 */
export const getMenuItems = (userPermissions?: string[]): MenuItem[] => {
  // 如果没有权限信息，只显示无权限要求的菜单
  if (!userPermissions || userPermissions.length === 0) {
    return menuConfigs.filter(item => !item.permission) as MenuItem[];
  }

  const filteredMenus = filterMenuItems(menuConfigs, userPermissions);
  console.debug('[Menu Debug] userPermissions:', userPermissions);
  console.debug('[Menu Debug] filteredMenus:', filteredMenus);

  return filteredMenus;
};

// 重新导出权限检查函数供外部使用
export { checkPermission } from './permissions/menuPermissions';
